import { Button, PasswordInput, Stack, TextInput, Text } from "@mantine/core";
import { isEmail, isNotEmpty, useForm } from "@mantine/form";

import { useTranslation } from "react-i18next";
import useAuthStore from "../../store/useAuthStore";
import { useNavigate } from "react-router";
import { useState } from "react";

export const SignInForm = ({
  isSocialLoading,
}: {
  isSocialLoading: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: isEmail(t("invalid_email_address")),
      password: isNotEmpty(t("password_required")),
    },
  });

  const { login, setIsLoggedIn } = useAuthStore();

  const focusNextInput = (currentName: string) => {
    const fieldOrder = ["email", "password"];
    const currentIndex = fieldOrder.indexOf(currentName);
    const nextField = fieldOrder[currentIndex + 1];
    if (nextField) {
      const next = document.querySelector(
        `input[name="${nextField}"]`
      ) as HTMLElement | null;
      next?.focus();
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    setErrorMessage(null);
    setIsLoading(true);
    try {
      const successLogin = await login({
        email: values.email.toLowerCase(),
        password: values.password,
      });

      if (successLogin) {
        setIsLoggedIn(true);
        navigate("/");
      } else {
        setErrorMessage(t("login_failed"));
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Login error:", error);
      setErrorMessage(t("incorrect_credentials"));
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack align="stretch" justify="center" gap="24px">
        <TextInput
          name="email"
          placeholder={t("email_placeholder")}
          label={t("email_label")}
          {...form.getInputProps("email")}
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const valid = form.validateField("email");
              if (valid.hasError) return;
              focusNextInput("email");
            }
          }}
        />
        <PasswordInput
          name="password"
          placeholder={t("password_placeholder")}
          label={t("password_label")}
          {...form.getInputProps("password")}
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const valid = form.validateField("password");
              if (valid.hasError) return;
              form.onSubmit(handleSubmit)();
            }
          }}
        />

        {errorMessage && (
          <Text color="red" size="sm">
            {errorMessage}
          </Text>
        )}

        <Button fullWidth type="submit" loading={isSocialLoading || isLoading}>
          {t("log_in_button")}
        </Button>
      </Stack>
    </form>
  );
};
